import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { addDays } from 'date-fns';
import getDate from 'date-fns/getDate';
import parseISO from 'date-fns/parseISO';
import { observer } from 'mobx-react';

import { paymentMethod, originalGSNICStates } from '@ourbranch/lookups';
import { useStore } from 'core/store';
import Section from 'core/components/section';
import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import { FormField } from 'core/components/form';
import { NotificationCard } from 'core/components/notification-card';
import withDatePicker from 'core/components/with-date-picker';
import { canOfferMegaDownPay } from '../../helpers/mega-down-pay-helpers';
import useStyles from './settings.styles';

const RentersSettings = observer(function RentersSettings({
  paymentMethodOptions,
  billingDaysOptions,
  paymentTypeOptions
}) {
  const { offer } = useStore();
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const { isBix } = offer;

  const offerMegaDownPay = useMemo(
    () =>
      canOfferMegaDownPay(
        values.global.rentersEffectiveDate,
        values.global.rentersBillingDayOfMonth,
        values.global.rentersPaymentType
      ),
    [values.global.rentersEffectiveDate, values.global.rentersBillingDayOfMonth, values.global.rentersPaymentType]
  );

  const state = offer?.state;

  const showCreditCardNote =
    values.global.rentersPaymentMethod === paymentMethod.ACH && !isBix && originalGSNICStates.includes(state);

  // if renters billing day of month is null, default select the effective day of the policy
  // or the 28th if the effectiveDay is past the allowed options of days 1 - 28
  useEffect(() => {
    const rentersEffectiveDay = getDate(parseISO(values.global.rentersEffectiveDate));
    if (!values.global.rentersBillingDayOfMonth && values.global.rentersBillingDayOfMonth !== rentersEffectiveDay) {
      if (rentersEffectiveDay > 28) {
        setFieldValue('global.rentersBillingDayOfMonth', 28);
      } else {
        setFieldValue('global.rentersBillingDayOfMonth', rentersEffectiveDay);
      }
    }
  }, []);

  return (
    <Section title="Renters Policy Settings" type="SubSection">
      <Card className={classes.container} type="secondary">
        <Label type="formSubTitle">Policy Term</Label>
        <Grid container alignItems="center">
          <FormField
            type="date"
            name="global.rentersEffectiveDate"
            label="Renters policy effective date"
            xs={4}
            mode="dark"
            disableFuture={false}
            maxDate={addDays(new Date(), 60)}
            fast={false}
          />
        </Grid>
        <Grid item container alignItems="center" xs={12} spacing={2}>
          <Grid item xs={2}>
            <Label type="formSubTitle" className={classes.subtitle}>
              Billing Details
            </Label>
          </Grid>
          {showCreditCardNote && (
            <Grid item xs={6}>
              <Label type="noteSmall">Note: There is no additional fee for paying with credit card.</Label>
            </Grid>
          )}
        </Grid>
        <Grid container>
          <Grid container item xs={12} spacing={4}>
            <FormField
              type="select"
              name="global.rentersBillingDayOfMonth"
              label="Renters Billing Day"
              mode="dark"
              options={billingDaysOptions}
              xs={4}
              fast={false}
            />
            <FormField
              label="Renters payment method"
              xs={4}
              type="select"
              name="global.rentersPaymentMethod"
              options={paymentMethodOptions}
              mode="dark"
            />
            <FormField
              label="Renters payment frequency"
              xs={4}
              type="select"
              name="global.rentersPaymentType"
              options={paymentTypeOptions}
              mode="dark"
              fast={false}
            />
          </Grid>
        </Grid>
        {offerMegaDownPay && (
          <>
            <Grid container>
              <FormField
                type="checkbox"
                name="global.rentersMegaDownPay"
                id="global.rentersMegaDownPay"
                label="Add second payment to down payment billing"
                fast={false}
              />
            </Grid>
            {!values.global.rentersMegaDownPay && (
              <NotificationCard type="quaternary">
                <Label className={classes.warningText}>
                  The member will be charged a second payment within 20 days of making their down payment.
                </Label>
              </NotificationCard>
            )}
          </>
        )}
      </Card>
    </Section>
  );
});

RentersSettings.propTypes = {
  paymentMethodOptions: PropTypes.array.isRequired,
  billingDaysOptions: PropTypes.array.isRequired,
  paymentTypeOptions: PropTypes.array.isRequired
};

export default withDatePicker(RentersSettings);
